import { Button } from "@/components/ui/button";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { inviteFriendToEvent } from "@/lib/invitation";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Gift, MessageCircle, Share2, TicketPlus, X } from "lucide-react";
import { useCallback, useState } from "react";
import { useTracking } from "@/hooks/useInteractionTracking";
import { useCancellationPolicy } from "@/hooks/useCancellationPolicy";
import { TicketCancelDialog } from "./TicketCancelDialog";
import { useFriendSeatsRemaining } from "@/hooks/useBringFriend";
import BringFriendPanel from "./BringFriendPanel";
import { googleCalendarLink } from "@shared/departure";
import { BOOKED_GROUP_SELECT, toBookedDeparture, type BookedEvent, type BookedGroup } from "@/lib/bookedDeparture";
import { formatGroupTime } from "@/lib/eventGroups";
import { userTicketsBookedQueryKey } from "@/lib/ticketQueryKeys";
import { Calendar } from "lucide-react";

interface BookedTicket {
  id: string;
  payment_method: string | null;
  credits_spent: number | null;
  total_price: number | null;
  status: string;
  group_id: string | null;
  events: (BookedEvent & { description?: string | null; timezone?: string | null }) | null;
  event_groups: BookedGroup | null;
}

interface BookedEventActionsProps {
  eventId: string;
  eventTitle: string;
  ticketId: string;
  onBookMore?: () => void;
  bringFriendEligible?: boolean;
}

const BookedEventActions = ({ eventId, eventTitle, ticketId, onBookMore, bringFriendEligible }: BookedEventActionsProps) => {
  const { data: userProfile, isLoading: isLoadingUserProfile } = useProfile();
  const { trackClick } = useTracking();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data: myTickets } = useQuery({
    // Separate key from EventBooking's single-object ['user-ticket', …] —
    // this query returns an array of all confirmed tickets.
    queryKey: userTicketsBookedQueryKey(eventId),
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // A user can hold several confirmed tickets (possibly in different waves);
      // newest first so the primary ticket is deterministic.
      const { data, error } = await supabase
        .from('tickets')
        .select(`
          id,
          payment_method,
          credits_spent,
          total_price,
          status,
          group_id,
          events (
            title,
            description,
            venue,
            whatsapp_group,
            start_time,
            end_time,
            timezone,
            host_id,
            secondary_host_id
          ),
          ${BOOKED_GROUP_SELECT}
        `)
        .eq('event_id', eventId)
        .eq('user_id', user.id)
        .eq('status', 'confirmed')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as unknown as BookedTicket[];
    },
  });
  const ticketInfo = myTickets?.[0];
  const event = ticketInfo?.events ?? null;

  // Only hide the £5-off referral invite while the booker still has free
  // friend seats left — otherwise they'd be left with no invite option at all.
  const { remaining: friendSeatsRemaining } = useFriendSeatsRemaining(
    Boolean(bringFriendEligible),
    eventId,
  );
  const showReferralInvite = !bringFriendEligible || friendSeatsRemaining <= 0;

  const startTime = ticketInfo?.events?.start_time;
  const policy = useCancellationPolicy(startTime);
  const isPastEvent = startTime ? new Date(startTime).getTime() <= Date.now() : false;
  const showCancel = Boolean(ticketInfo?.id) && !isPastEvent;

  // Distinct waves the user holds, earliest first. Empty for ungrouped tickets.
  const heldGroups = Array.from(
    new Map(
      (myTickets ?? [])
        .filter((t) => t.event_groups)
        .map((t) => [t.event_groups!.id, t.event_groups!] as const),
    ).values(),
  ).sort((a, b) => (a.start_time ?? "").localeCompare(b.start_time ?? ""));
  // Host profiles aren't rendered on this bar, so skip the profile fetch and
  // pass no host map (labels/chat/calendar only read `resolved`).
  const departures = event ? heldGroups.map((g) => toBookedDeparture(event, g, undefined)) : [];
  const primaryDeparture = event ? toBookedDeparture(event, ticketInfo?.event_groups ?? null, undefined) : null;
  const chatLink = primaryDeparture?.resolved.whatsapp_group || event?.whatsapp_group || null;
  const tz = event?.timezone || "Europe/London";
  const departureLabel = departures
    .map((d) => `${formatGroupTime(d.resolved.start_time, tz)}${d.resolved.group_name ? ` (${d.resolved.group_name})` : ""}`)
    .join(", ");

  const handleEventChatClick = useCallback(() => {
    if (chatLink) {
      trackClick("whatsapp_event_chat", { eventId });
      window.open(chatLink, '_blank');
    }
  }, [chatLink, eventId, trackClick]);

  const handleAddToCalendar = useCallback(() => {
    if (!event || !primaryDeparture) return;
    trackClick("add_to_calendar_booked", { eventId });
    window.open(googleCalendarLink({
      title: event.title ?? eventTitle,
      start: primaryDeparture.resolved.start_time,
      end: primaryDeparture.resolved.end_time || primaryDeparture.resolved.start_time,
      details: event.description,
      location: primaryDeparture.resolved.venue,
      groupName: primaryDeparture.resolved.group_name,
    }), '_blank');
  }, [event, primaryDeparture, eventId, eventTitle, trackClick]);

  const handleInviteFriend = useCallback(async () => {
    const referralCode = userProfile?.referral_code;
    if (!referralCode) return;
    trackClick("invite_friend_event", { eventId });
    inviteFriendToEvent({ eventId, referralCode });
  }, [eventId, userProfile?.referral_code, trackClick]);

  return (
    <div className="fixed bottom-20 left-0 right-0 z-40 bg-card border-t border-border p-4">
      <div className="max-w-md mx-auto max-h-[calc(100vh-7rem)] overflow-y-auto">
        <div className="flex items-center justify-between gap-4">
          {departures.length > 0 ? (
            <div className="min-w-0">
              <div className="text-lg font-medium text-primary">You're going!</div>
              <div className="text-sm text-muted-foreground truncate">{departureLabel}</div>
            </div>
          ) : (
            <div className="text-lg font-medium text-primary">You're going!</div>
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="gap-2"
              onClick={handleEventChatClick}
            >
              <MessageCircle className="w-4 h-4" />
              Event Chat
            </Button>
            {showReferralInvite && (
              <Button
                variant="outline"
                className="gap-2"
                onClick={handleInviteFriend}
                disabled={!userProfile?.referral_code || isLoadingUserProfile}
              >
                <Share2 className="w-4 h-4" />
                Invite Friend (£5 Off)
              </Button>
            )}
          </div>
        </div>
        {departures.length > 0 && (
          <Button variant="outline" className="w-full mt-3 gap-2" onClick={handleAddToCalendar}>
            <Calendar className="w-4 h-4" />
            Add to Calendar
          </Button>
        )}
        {bringFriendEligible && (
          <>
            <Button
              variant="outline"
              className="w-full mt-3 gap-2"
              onClick={() => setInviteOpen((open) => !open)}
              aria-expanded={inviteOpen}
              aria-controls="bring-friend-panel"
            >
              <Gift className="w-4 h-4" />
              Bring a Friend for Free
              <ChevronDown className={`ml-auto w-4 h-4 transition-transform ${inviteOpen ? "rotate-180" : ""}`} />
            </Button>
            {inviteOpen && (
              <div className="mt-3">
                <BringFriendPanel
                  eventId={eventId}
                  ticketId={ticketId}
                  eventTitle={eventTitle}
                  embedded
                />
              </div>
            )}
          </>
        )}
        {onBookMore && (
          <Button
            variant="secondary"
            className="w-full mt-3 gap-2"
            onClick={onBookMore}
          >
            <TicketPlus className="w-4 h-4" />
            Book More Tickets
          </Button>
        )}
        {showCancel && (
          <Button
            variant="ghost"
            className="w-full mt-2 gap-2 text-muted-foreground hover:text-destructive"
            onClick={() => {
              trackClick("cancel_booking_open", { eventId });
              setCancelOpen(true);
            }}
          >
            <X className="w-4 h-4" />
            Cancel booking
          </Button>
        )}
      </div>

      {showCancel && ticketInfo?.id && (
        <TicketCancelDialog
          open={cancelOpen}
          onOpenChange={setCancelOpen}
          ticketId={ticketInfo.id}
          eventId={eventId}
          paymentMethod={ticketInfo.payment_method as string | null}
          creditsSpent={ticketInfo.credits_spent}
          totalPrice={ticketInfo.total_price}
          policy={policy}
        />
      )}
    </div>
  );
};

export default BookedEventActions;
